/**
 * WhatsApp Message Types
 *
 * Clean type definitions for all WhatsApp message types.
 * Single source of truth for message structures.
 */

// =============================================================================
// SENDER TYPES
// =============================================================================

export const SENDER_TYPE = {
  USER: 1,
  BOT: 2,
  AGENT: 3,
} as const;

export type SenderType = (typeof SENDER_TYPE)[keyof typeof SENDER_TYPE];

// =============================================================================
// HEADER TYPES
// =============================================================================

export type HeaderType = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';

// =============================================================================
// BUTTON TYPES
// =============================================================================

export type ButtonType = 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY';

export interface TemplateButton {
  type: ButtonType;
  text: string;
  url?: string;
  phone_number?: string;
}

// =============================================================================
// INTERACTIVE TYPES
// =============================================================================

export type InteractiveType = 'button';

export interface InteractiveButton {
  id: string;
  title: string;
}

// =============================================================================
// CAROUSEL TYPES
// =============================================================================

export interface CarouselCard {
  card_index: number;
  header_format: 'IMAGE' | 'VIDEO';
  header_media_url?: string;
  header_media_handle?: string;
  title?: string;
  body?: string;
  body_variables?: string[];
  buttons?: TemplateButton[];
}

// =============================================================================
// REACTION TYPES
// =============================================================================

export interface MessageReaction {
  emoji: string;
  from: string;
  timestamp?: string;
}

// =============================================================================
// MESSAGE STATUS
// =============================================================================

export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

// =============================================================================
// MAIN MESSAGE INTERFACE
// =============================================================================

export interface WhatsAppMessage {
  message_id: string;
  conversation_id: string;
  sender_type: SenderType;
  date_time: string;
  status?: MessageStatus;

  // Content
  text: string;
  markdown_text?: string;

  // Header
  header_type?: HeaderType;
  header_text?: string;
  header_media_url?: string;

  // Footer
  footer?: string;
  footer_text?: string;

  // Template Buttons (URL, Phone, Quick Reply)
  buttons?: TemplateButton[];

  // Carousel
  carousel_cards?: CarouselCard[];

  // Interactive (Quick Replies - session messages)
  interactive_type?: InteractiveType;
  interactive_buttons?: InteractiveButton[];

  // Reactions
  reactions?: MessageReaction[];

  // Reply context (when user taps a button)
  interactive_reply_id?: string;

  // Send error (when WhatsApp API rejects the message)
  send_error?: string;
}

// =============================================================================
// COMPONENT PROPS
// =============================================================================

export interface MessageCardProps {
  message: WhatsAppMessage;
  isOutbound: boolean;
  onButtonClick?: (button: TemplateButton) => void;
  onInteractiveButtonClick?: (buttonId: string, buttonTitle: string) => void;
}
